import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useModuleProgress from '../hooks/useModuleProgress';
import '../styles/pages/_module-pages.scss';
import '../styles/pages/_learn-assessments.scss';
import '../styles/pages/_quiz-completion.scss';

interface QuizQuestion {
  question: string;
  options: string[];
  correctAnswer: number;
  explanation: string;
}

const quizQuestions: QuizQuestion[] = [
  {
    question: "What does 'Connectedness' mean in the context of spiritual wellness?",
    options: [
      "Being constantly online and connected to social media",
      "Feeling a deep sense of belonging and relationship with all life",
      "Having many friends and social connections",
      "Being dependent on others for your happiness"
    ],
    correctAnswer: 1,
    explanation: "Connectedness in spiritual wellness refers to feeling a deep sense of belonging and relationship with all life. It's about recognizing our interconnectedness with nature, other people, and the universe itself."
  },
  {
    question: "According to the module, building relationships with life involves:",
    options: [
      "Only human relationships",
      "Recognizing the sacredness in everyday moments and all living things",
      "Ignoring nature and focusing only on people",
      "Following strict relationship rules"
    ],
    correctAnswer: 1,
    explanation: "Building relationships with life involves recognizing the sacredness in everyday moments and all living things. This includes our relationship with nature, animals, ourselves, and the world around us."
  },
  {
    question: "What is the benefit of cultivating connectedness in daily life?",
    options: [
      "It makes you less empathetic",
      "It reduces feelings of loneliness and isolation",
      "It makes you more self-centered",
      "It has no impact on mental health"
    ],
    correctAnswer: 1,
    explanation: "Cultivating connectedness reduces feelings of loneliness and isolation. When we feel connected to something greater than ourselves, we experience less separation and more belonging."
  },
  {
    question: "The module suggests that we can experience connectedness through:",
    options: [
      "Only formal spiritual practices",
      "Simple acts like walking in nature, mindful eating, and appreciating beauty",
      "Ignoring our surroundings",
      "Focusing only on material possessions"
    ],
    correctAnswer: 1,
    explanation: "We can experience connectedness through simple, everyday acts like walking in nature, eating mindfully, and appreciating the beauty around us. These moments help us feel our connection to the world."
  },
  {
    question: "What is the relationship between connectedness and compassion?",
    options: [
      "They are completely separate",
      "Connectedness naturally leads to greater compassion for others",
      "Connectedness makes you less compassionate",
      "Compassion has no connection to spiritual wellness"
    ],
    correctAnswer: 1,
    explanation: "Connectedness naturally leads to greater compassion for others. When we recognize our shared humanity and interconnectedness with all life, we become more understanding and caring toward others."
  }
];

const SpiritualModule2QuizPage: React.FC = () => {
  const navigate = useNavigate();
  const { currentStep, setCurrentStep, isLoading, saveProgress } = useModuleProgress('spiritualM2');
  const [currentQuestion, setCurrentQuestion] = useState<number>(0);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [showExplanation, setShowExplanation] = useState(false);
  const [responses, setResponses] = useState<{[key: number]: number}>({});
  const [showCompletion, setShowCompletion] = useState(false);

  const handleAnswerSelect = (answerIndex: number) => {
    if (responses[currentQuestion] === undefined) {
      setSelectedAnswer(answerIndex);
      setShowExplanation(true);
      setResponses(prev => ({...prev, [currentQuestion]: answerIndex}));
    }
  };

  const handleNext = () => {
    if (currentQuestion < quizQuestions.length - 1) {
      const nextQuestion = currentQuestion + 1;
      setCurrentQuestion(nextQuestion);
      setSelectedAnswer(null);
      setShowExplanation(false);
      saveProgress(nextQuestion + 1);
    } else {
      saveProgress(quizQuestions.length, true);
      setShowCompletion(true);
    }
  };

  const handleBack = () => {
    if (currentQuestion > 0) {
      const prevQuestion = currentQuestion - 1;
      const previousResponse = responses[prevQuestion];
      setCurrentQuestion(prevQuestion);
      setSelectedAnswer(previousResponse ?? null);
      setShowExplanation(previousResponse !== undefined);
      saveProgress(prevQuestion + 1);
    }
  };

  const handleExit = () => {
    navigate('/panel/learn/spiritual-resources');
  };

  if (showCompletion) {
    return (
      <div className="quiz-completion-section">
        <h2 className="completion-title">
          <span>Congratulations!</span>
        </h2>
        <p className="completion-subtitle">
          You've successfully completed the Module 2 quiz!
        </p>

        <div className="completion-card">
          <div className="achievement-section">
            <h3 className="section-title">
              <span>Key Achievements</span>
            </h3>
            <p className="achievement-intro">You've demonstrated a great understanding of:</p>
            <ul className="achievement-list">
              <li>
                <span className="check">✓</span>
                <span>The meaning of connectedness</span>
              </li>
              <li>
                <span className="check">✓</span>
                <span>Building relationships with life</span>
              </li>
              <li>
                <span className="check">✓</span>
                <span>Daily practices for connection</span>
              </li>
            </ul>
          </div>

          <div className="next-steps-section">
            <h3 className="section-title">
              <span>Next Steps</span>
            </h3>
            <p>Continue your journey by exploring mindfulness practices!</p>
            <p>Ready to cultivate greater presence and awareness?</p>
          </div>

          <button
            className="resource-button"
            onClick={handleExit}
          >
            Return to Spiritual Resources
          </button>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="module-form" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <p>Loading your progress...</p>
      </div>
    );
  }

  const progress = ((currentQuestion + 1) / quizQuestions.length) * 100;
  const currentQuiz = quizQuestions[currentQuestion];

  return (
    <div className="module-form">
      <div className="module-container">
        <div className="module-left spiritual-panel">
          <div className="module-header">
            <h1>Module 2 Quiz</h1>
            <p className="module-subtitle">Connectedness – Building Relationship with Life</p>
            <hr className="module-divider" />
            <p className="module-tool-info">
              Let's review what we've learned in a stress-free way.
              There's no scoring - this is just to reinforce the main ideas!
            </p>
          </div>
        </div>

        <div className="assessment-right-panel">
          <div className="assessment-content">
            <div className="module-progress">
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${progress}%` }}></div>
              </div>
              <span className="progress-text">
                Question {currentQuestion + 1} of {quizQuestions.length}
              </span>
            </div>

            <div className="question-section">
              <h2 className="question-title">
                {currentQuiz.question}
              </h2>
              <p className="question-instruction">
                Please select the answer that best matches your understanding.
              </p>

              <div className="answer-options">
                {currentQuiz.options.map((option, index) => (
                  <label key={index} className="answer-option">
                    <input
                      type="radio"
                      name={`question-${currentQuestion}`}
                      value={index}
                      checked={selectedAnswer === index}
                      onChange={() => handleAnswerSelect(index)}
                      disabled={selectedAnswer !== null}
                    />
                    <span className="option-text">{option}</span>
                  </label>
                ))}
              </div>

              {showExplanation && (
                <div className="explanation">
                  <h3>{selectedAnswer === currentQuiz.correctAnswer ? 'Correct!' : 'Remember:'}</h3>
                  <p>{currentQuiz.explanation}</p>
                </div>
              )}
            </div>

            <div className="navigation-buttons">
              <button
                className="btn-secondary"
                onClick={currentQuestion === 0 ? handleExit : handleBack}
              >
                {currentQuestion === 0 ? 'Exit Quiz' : 'Previous Question'}
              </button>

              {currentQuestion < quizQuestions.length - 1 ? (
                <button
                  className="btn-primary"
                  onClick={handleNext}
                  disabled={selectedAnswer === null}
                >
                  Next Question
                </button>
              ) : (
                <button
                  className="btn-primary"
                  onClick={handleNext}
                  disabled={selectedAnswer === null}
                >
                  Complete Quiz
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SpiritualModule2QuizPage;

